import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { HiArrowLeft, HiCloudUpload, HiPlus, HiOutlineDocumentDuplicate, HiOutlineTrash } from 'react-icons/hi';
import { HiOutlineArrowUpTray } from 'react-icons/hi2';
import { FaEdit } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { UpdateEvent } from '../../redux/actions/Events';
import { ApiURL } from '../../setting/GlobleVariable';
import axios from 'axios';
import './CreateEventPage.css';

const EditEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState(null);
  const [formData, setFormData] = useState({
    eventTitle: '',
    category: '',
    type: '',
    eventFormat: '',
    venueAddress: '',
    eventLink: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    description: '',
    ticketPrice: '',
    maxTickets: '',
    userTickets: []
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImage, setCurrentImage] = useState('');
  const [hasTicketsSold, setHasTicketsSold] = useState(false);

  // Debug form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
  }, [formData]);

  // Fetch event data on component mount
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        console.log('Fetching event with ID:', eventId);
        
        const response = await axios.get(`${ApiURL}/event/edit/${eventId}`, {
          headers: {
            token: localStorage.getItem("instapass_user_token"),
          },
        });
        
        console.log('API Response:', response.data);
        
        if (response.data.success && response.data.event) {
          const eventData = response.data.event;
          setEvent(eventData);
          setCurrentImage(eventData.image);
          setHasTicketsSold(eventData.ticketsSold > 0);
          
          // Process userTickets - ensure they have the correct structure for editing
          const processedTickets = eventData.userTickets?.map(ticket => ({
            id: ticket.id || Date.now() + Math.random(),
            ticketName: ticket.ticketName || ticket.name || '',
            quantity: ticket.quantity || '',
            price: ticket.price || '',
            serviceFee: ticket.serviceFee || 'pass-to-buyer',
            salesStartOption: ticket.salesStartOption || 'immediately',
            salesStartDate: ticket.salesStartDate || '',
            salesStartTime: ticket.salesStartTime || '',
            salesEndOption: ticket.salesEndOption || 'event-start',
            salesEndDate: ticket.salesEndDate || '',
            salesEndTime: ticket.salesEndTime || '',
            sold: ticket.sold || 0
          })) || [];
          
          // Determine event format based on available data
          let eventFormat = eventData.eventFormat || '';
          if (!eventFormat) {
            if (eventData.venue || eventData.venueAddress) {
              eventFormat = 'physical';
            } else if (eventData.eventLink) {
              eventFormat = 'online';
            } else {
              eventFormat = 'physical'; // default
            }
          }

          // Handle category - it might be an object or string
          let category = eventData.category || '';
          if (typeof category === 'object' && category.slug) {
            // Map API category slugs to our form values
            const categoryMapping = {
              'classical': 'music',
              'concerts': 'music',
              'jazz': 'music',
              'rock': 'music',
              'electronic': 'music',
              'pop': 'music',
              'basketball': 'sports',
              'football': 'sports',
              'baseball': 'sports',
              'soccer': 'sports',
              'tennis': 'sports',
              'theater': 'arts-theater',
              'comedy': 'arts-theater',
              'dance': 'arts-theater',
              'opera': 'arts-theater',
              'musical': 'arts-theater',
              'music-festival': 'festivals',
              'food-festival': 'festivals',
              'cultural': 'festivals',
              'art-fair': 'festivals',
              'webinar': 'webinar',
              'workshop': 'workshop',
              'conference': 'conference',
              'networking': 'networking',
              'training': 'training'
            };
            category = categoryMapping[category.slug] || category.slug || 'other';
          }

          // Populate form with existing data
          setFormData(prevData => ({
            ...prevData,
            eventTitle: eventData.eventTitle || eventData.name || '',
            category: category,
            type: eventData.type || '',
            eventFormat: eventFormat,
            venueAddress: eventData.venueAddress || eventData.venue?.location || '',
            eventLink: eventData.eventLink || eventData.link || '',
            startDate: eventData.startDate || (eventData.occurs_at ? new Date(eventData.occurs_at).toISOString().split('T')[0] : ''),
            startTime: eventData.startTime || (eventData.occurs_at ? new Date(eventData.occurs_at).toTimeString().slice(0, 5) : ''),
            endDate: eventData.endDate || (eventData.ends_at ? new Date(eventData.ends_at).toISOString().split('T')[0] : ''),
            endTime: eventData.endTime || (eventData.ends_at ? new Date(eventData.ends_at).toTimeString().slice(0, 5) : ''),
            description: eventData.paragarph || eventData.description || '',
            ticketPrice: eventData.ticketPrice || '',
            maxTickets: eventData.maxTickets || '',
            userTickets: processedTickets
          }));
          
          console.log('Form data set with:', {
            category: category,
            eventFormat: eventFormat,
            eventTitle: eventData.eventTitle || eventData.name || ''
          });
        } else {
          throw new Error('Invalid API response structure');
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        toast.error(error.response?.data?.message || "Failed to fetch event details");
        navigate('/user/profile');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventData();
    }
  }, [eventId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleTicketChange = (index, field, value) => {
    const updatedTickets = [...formData.userTickets];
    updatedTickets[index] = {
      ...updatedTickets[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      userTickets: updatedTickets
    }));
  };

  const updateTicket = (ticketId, field, value) => {
    setFormData(prev => ({
      ...prev,
      userTickets: prev.userTickets.map(ticket => 
        ticket.id === ticketId ? { ...ticket, [field]: value } : ticket
      )
    }));
  };

  const addTicketType = () => {
    const newTicket = {
      id: Date.now() + Math.random(),
      ticketName: '',
      quantity: '',
      price: '',
      serviceFee: 'pass-to-buyer',
      salesStartOption: 'immediately',
      salesStartDate: '',
      salesStartTime: '',
      salesEndOption: 'event-start',
      salesEndDate: '',
      salesEndTime: '',
      sold: 0
    };

    setFormData(prev => ({
      ...prev,
      userTickets: [...prev.userTickets, newTicket]
    }));
  };

  const duplicateTicket = (ticketId) => {
    const ticketToDuplicate = formData.userTickets.find(t => t.id === ticketId);
    if (ticketToDuplicate) {
      const duplicatedTicket = {
        ...ticketToDuplicate,
        id: Date.now() + Math.random(),
        ticketName: ticketToDuplicate.ticketName + ' (Copy)',
        sold: 0 // New duplicated ticket should have no sales
      };
      setFormData(prev => ({
        ...prev,
        userTickets: [...prev.userTickets, duplicatedTicket]
      }));
    }
  };

  const deleteTicket = (ticketId) => {
    const ticket = formData.userTickets.find(t => t.id === ticketId);
    if (ticket && ticket.sold > 0) {
      toast.error("Cannot delete ticket type that has already been sold");
      return;
    }

    setFormData(prev => ({
      ...prev,
      userTickets: prev.userTickets.filter(t => t.id !== ticketId)
    }));
  };

  const getTotalCapacity = () => {
    return formData.userTickets.reduce((sum, ticket) => sum + (parseInt(ticket.quantity) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.eventTitle.trim()) {
      toast.error("Event title is required");
      return;
    }

    setSubmitting(true);
    try {
      // Prepare event data for submission
      const eventDataForSubmission = {
        ...formData,
        occurs_at: formData.startDate && formData.startTime 
          ? new Date(`${formData.startDate}T${formData.startTime}`).toISOString()
          : null,
        ends_at: formData.endDate && formData.endTime 
          ? new Date(`${formData.endDate}T${formData.endTime}`).toISOString()
          : null,
        // Ensure userTickets are included in the submission
        userTickets: formData.userTickets
      };

      const result = await dispatch(UpdateEvent(
        eventId, 
        eventDataForSubmission, 
        formData.description,
        selectedImage
      ));

      if (result.success) {
        toast.success("Event updated successfully!");
        navigate('/user/profile');
      } else {
        toast.error(result.message || "Failed to update event");
      }
    } catch (error) {
      toast.error("Failed to update event");
      console.error("Error updating event:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="create-event-page">
        <div className="create-event-container flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-redColor mx-auto mb-4"></div>
            <p>Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="create-event-page">
        <div className="create-event-container flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-xl mb-4">Event not found</p>
            <button
              onClick={() => navigate('/user/profile')}
              className="px-4 py-2 bg-redColor text-white rounded-md hover:opacity-90"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-event-page">
      <div className="create-event-container">
        <div className="create-event-header">
          <button className="back-btn" onClick={() => navigate('/user/profile')}>
            <HiArrowLeft size={24} />
            Back to Dashboard
          </button>
          <h1 className="create-event-title">
            <FaEdit className="inline mr-2" />
            Edit Event
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="create-event-form">
          {/* Event Basic Info */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            
            <div className="form-group">
              <label className="form-label">Event Title *</label>
              <input
                type="text"
                className="form-input"
                value={formData.eventTitle}
                onChange={(e) => handleInputChange('eventTitle', e.target.value)}
                placeholder="Enter your event title"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  key={`category-${formData.category}`}
                  className="form-input"
                  value={formData.category || ''}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                >
                  <option value="">Select Category</option>
                  <option value="music">Music</option>
                  <option value="sports">Sports</option>
                  <option value="arts-theater">Arts & Theater</option>
                  <option value="festivals">Festivals</option>
                  <option value="webinar">Webinar</option>
                  <option value="workshop">Workshop</option>
                  <option value="conference">Conference</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Event Format</label>
                <select
                  key={`format-${formData.eventFormat}`}
                  className="form-input"
                  value={formData.eventFormat || ''}
                  onChange={(e) => handleInputChange('eventFormat', e.target.value)}
                >
                  <option value="">Select Format</option>
                  <option value="physical">In-Person</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            {formData.eventFormat === 'physical' && (
              <div className="form-group">
                <label className="form-label">Venue Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.venueAddress}
                  onChange={(e) => handleInputChange('venueAddress', e.target.value)}
                  placeholder="Enter venue address"
                />
              </div>
            )}

            {formData.eventFormat === 'online' && (
              <div className="form-group">
                <label className="form-label">Event Link</label>
                <input
                  type="url"
                  className="form-input"
                  value={formData.eventLink}
                  onChange={(e) => handleInputChange('eventLink', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}
          </div>

          {/* Date & Time */}
          <div className="form-section">
            <h3 className="section-title">Date & Time</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Start Time</label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.startTime}
                  onChange={(e) => handleInputChange('startTime', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">End Time</label>
                <input
                  type="time"
                  className="form-input"
                  value={formData.endTime}
                  onChange={(e) => handleInputChange('endTime', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-section">
            <h3 className="section-title">Event Image</h3>
            
            {currentImage && !selectedImage && (
              <div className="current-image-preview">
                <img
                  src={`${ApiURL.replace('/api', '')}/uploads/${currentImage}`}
                  alt="Current event"
                />
                <p>Current image</p>
              </div>
            )}

            <div className="image-upload-area">
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="image-upload" className="image-upload-label">
                <HiOutlineArrowUpTray className="upload-icon" />
                <p className="upload-text">
                  {selectedImage ? selectedImage.name : "Click to upload new image"}
                </p>
                <p className="upload-subtext">PNG, JPG up to 10MB</p>
              </label>
            </div>
          </div>

          {/* Description */}
          <div className="form-section">
            <h3 className="section-title">Description</h3>
            <div className="form-group">
              <textarea
                className="form-input"
                rows="4"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your event..."
              />
            </div>
          </div>

          {/* Ticket Configuration */}
          <div className="form-section">
            <h3 className="section-title">Ticket Configuration</h3>
            {hasTicketsSold && (
              <div className="warning-banner mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded-md">
                <p className="text-yellow-800 text-sm">
                  ⚠️ Some tickets have been sold. You can only edit ticket details for types with no sales, 
                  and cannot delete ticket types that have been sold.
                </p>
              </div>
            )}

            {/* Total Capacity Summary */}
            {formData.userTickets.length > 0 && (
              <div className="capacity-summary">
                <div className="capacity-info">
                  <span className="capacity-label">Total Event Capacity:</span>
                  <span className="capacity-value">{getTotalCapacity()}</span>
                  <span className="capacity-label-text">tickets</span>
                </div>
                <div className="ticket-types-info">
                  <span className="capacity-label">Ticket Types:</span>
                  <span className="ticket-types-value">{formData.userTickets.length}</span>
                </div>
              </div>
            )}

            {/* Ticket Types */}
            <div className="tickets-container">
              {formData.userTickets.map((ticket, index) => {
                const hasTicketSales = ticket.sold > 0;
                return (
                  <div key={ticket.id} className={`ticket-type-card ${hasTicketSales ? 'sold-ticket' : ''}`}>
                    <div className="ticket-header">
                      <h4 className="ticket-title">
                        Ticket Type #{index + 1}
                        {hasTicketSales && <span className="sold-indicator">({ticket.sold} sold)</span>}
                      </h4>
                      <div className="ticket-actions">
                        <button
                          type="button"
                          onClick={() => duplicateTicket(ticket.id)}
                          className="ticket-action-btn"
                          title="Duplicate ticket"
                        >
                          <HiOutlineDocumentDuplicate size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTicket(ticket.id)}
                          className={`ticket-action-btn delete ${hasTicketSales ? 'disabled' : ''}`}
                          title={hasTicketSales ? "Cannot delete - tickets sold" : "Delete ticket"}
                          disabled={hasTicketSales}
                        >
                          <HiOutlineTrash size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Ticket Name */}
                    <div className="form-group">
                      <label className="form-label">Ticket Name *</label>
                      <input
                        type="text"
                        className={`form-input ${hasTicketSales ? 'disabled-input' : ''}`}
                        placeholder="e.g., General Admission, VIP, Early Bird"
                        value={ticket.ticketName}
                        onChange={(e) => updateTicket(ticket.id, 'ticketName', e.target.value)}
                        disabled={hasTicketSales}
                      />
                    </div>

                    {/* Quantity and Price */}
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Quantity Available *</label>
                        <input
                          type="number"
                          className={`form-input ${hasTicketSales ? 'disabled-input' : ''}`}
                          placeholder="100"
                          min={ticket.sold || "1"}
                          value={ticket.quantity}
                          onChange={(e) => updateTicket(ticket.id, 'quantity', e.target.value)}
                          disabled={hasTicketSales}
                        />
                        {hasTicketSales && (
                          <p className="form-help">
                            Minimum: {ticket.sold} (already sold)
                          </p>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Price *</label>
                        <div className="price-input-wrapper">
                          <span className="price-currency">$</span>
                          <input
                            type="number"
                            className={`form-input price-input ${hasTicketSales ? 'disabled-input' : ''}`}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            value={ticket.price}
                            onChange={(e) => updateTicket(ticket.id, 'price', e.target.value)}
                            disabled={hasTicketSales}
                          />
                        </div>
                        <p className="form-help">Enter 0 for free tickets</p>
                      </div>
                    </div>

                    {/* Service Fee */}
                    <div className="form-group">
                      <label className="form-label">Service Fee</label>
                      <div className="service-fee-buttons">
                        <button
                          type="button"
                          className={`service-fee-btn ${ticket.serviceFee === 'pass-to-buyer' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                          onClick={() => !hasTicketSales && updateTicket(ticket.id, 'serviceFee', 'pass-to-buyer')}
                          disabled={hasTicketSales}
                        >
                          Pass to buyer
                        </button>
                        <button
                          type="button"
                          className={`service-fee-btn ${ticket.serviceFee === 'absorb' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                          onClick={() => !hasTicketSales && updateTicket(ticket.id, 'serviceFee', 'absorb')}
                          disabled={hasTicketSales}
                        >
                          I'll absorb the fee
                        </button>
                      </div>
                    </div>

                    {/* Sales Window */}
                    <div className="sales-window-section">
                      <h4 className="section-subtitle">Sales Window</h4>
                      
                      {/* When do sales start */}
                      <div className="form-group">
                        <label className="form-label">When do sales start?</label>
                        <div className="sales-window-buttons">
                          <button
                            type="button"
                            className={`sales-window-btn ${ticket.salesStartOption === 'immediately' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                            onClick={() => !hasTicketSales && updateTicket(ticket.id, 'salesStartOption', 'immediately')}
                            disabled={hasTicketSales}
                          >
                            Immediately
                          </button>
                          <button
                            type="button"
                            className={`sales-window-btn ${ticket.salesStartOption === 'scheduled' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                            onClick={() => !hasTicketSales && updateTicket(ticket.id, 'salesStartOption', 'scheduled')}
                            disabled={hasTicketSales}
                          >
                            Scheduled
                          </button>
                        </div>
                        
                        {ticket.salesStartOption === 'scheduled' && (
                          <div className="datetime-row">
                            <input
                              type="date"
                              className={`form-input datetime-input ${hasTicketSales ? 'disabled-input' : ''}`}
                              value={ticket.salesStartDate}
                              onChange={(e) => updateTicket(ticket.id, 'salesStartDate', e.target.value)}
                              disabled={hasTicketSales}
                            />
                            <input
                              type="time"
                              className={`form-input datetime-input ${hasTicketSales ? 'disabled-input' : ''}`}
                              value={ticket.salesStartTime}
                              onChange={(e) => updateTicket(ticket.id, 'salesStartTime', e.target.value)}
                              disabled={hasTicketSales}
                            />
                          </div>
                        )}
                      </div>

                      {/* When do sales end */}
                      <div className="form-group">
                        <label className="form-label">When do sales end?</label>
                        <div className="sales-window-buttons">
                          <button
                            type="button"
                            className={`sales-window-btn ${ticket.salesEndOption === 'event-start' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                            onClick={() => !hasTicketSales && updateTicket(ticket.id, 'salesEndOption', 'event-start')}
                            disabled={hasTicketSales}
                          >
                            At event start
                          </button>
                          <button
                            type="button"
                            className={`sales-window-btn ${ticket.salesEndOption === 'custom' ? 'active' : ''} ${hasTicketSales ? 'disabled' : ''}`}
                            onClick={() => !hasTicketSales && updateTicket(ticket.id, 'salesEndOption', 'custom')}
                            disabled={hasTicketSales}
                          >
                            Custom date
                          </button>
                        </div>
                        
                        {ticket.salesEndOption === 'custom' && (
                          <div className="datetime-row">
                            <input
                              type="date"
                              className={`form-input datetime-input ${hasTicketSales ? 'disabled-input' : ''}`}
                              value={ticket.salesEndDate}
                              onChange={(e) => updateTicket(ticket.id, 'salesEndDate', e.target.value)}
                              disabled={hasTicketSales}
                            />
                            <input
                              type="time"
                              className={`form-input datetime-input ${hasTicketSales ? 'disabled-input' : ''}`}
                              value={ticket.salesEndTime}
                              onChange={(e) => updateTicket(ticket.id, 'salesEndTime', e.target.value)}
                              disabled={hasTicketSales}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add Another Ticket Button */}
              <button type="button" className="add-ticket-btn" onClick={addTicketType}>
                <HiPlus size={20} />
                Add Another Ticket Type
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="submit"
              className={`next-btn active ${submitting ? 'opacity-50' : ''}`}
              disabled={submitting || !formData.eventTitle.trim()}
            >
              {submitting ? 'Updating...' : 'Update Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventPage;